import { useCallback, useMemo, useState } from "react";
import dateformat from "dateformat";
import { createUseStyles } from "react-jss";
import { Button as UIButton } from "@mui/material";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import AddIcon from "@mui/icons-material/Add";

import { ShippingRecord, ShippingRecordDispute } from "$types";
import { DEFAULT_DATE_FORMAT } from "$utils/utils";
import {
  canAddDispute,
  getShippingRecordDisputeTypeInfo,
} from "$utils/typeConstants/ShippingRecordDispute";

import { Text } from "$components";
import InfoContainer from "$components/InfoContainer";
import JTable from "$components/JTable/JTable";

import AddDisputePopin from "./AddDisputePopin";

type Props = {
  shippingRecord: ShippingRecord;
  fetchShippingRecord: () => void;
  classes: any;
  disabled?: boolean;
};

function ShippingRecordDisputeTableBlock({
  classes,
  shippingRecord,
  fetchShippingRecord,
  disabled = false,
}: Props) {
  const [isAddDisputeDrawerOpen, toggleAddDisputeDrawer] =
    useState<boolean>(false);
  const [selectedDispute, setSelectedDispute] =
    useState<ShippingRecordDispute>(undefined);

  const sortedDisputes = useMemo(
    () =>
      shippingRecord?.shippingRecordDisputes?.sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      ),
    [shippingRecord?.shippingRecordDisputes]
  );

  const localClasses = useStyles();

  const handleAddDispute = () => {
    toggleAddDisputeDrawer(true);
  };

  const handleSubmit = () => {
    toggleAddDisputeDrawer(false);
    setSelectedDispute(null);
    fetchShippingRecord();
  };

  const renderDisputeType = useCallback(
    (dispute: ShippingRecordDispute) => {
      const disputeTypeInfo = getShippingRecordDisputeTypeInfo(
        dispute,
        shippingRecord
      );
      return (
        <div className={localClasses.disputeTypeContainer}>
          <Text type="important">{disputeTypeInfo?.label}</Text>
          {disputeTypeInfo.location && <Text>{disputeTypeInfo?.location}</Text>}
        </div>
      );
    },
    [shippingRecord, localClasses]
  );

  const columns = useMemo(
    () => [
      {
        Header: "Date d'ouverture",
        minWidth: 90,
        maxWidth: 90,
        Cell: ({
          row: { original: dispute },
        }: {
          row: { original: ShippingRecordDispute };
        }) => {
          return (
            <Text>{dateformat(dispute?.createdAt, DEFAULT_DATE_FORMAT)}</Text>
          );
        },
      },
      {
        Header: "Type",
        minWidth: 100,
        maxWidth: 100,
        Cell: ({
          row: { original: dispute },
        }: {
          row: { original: ShippingRecordDispute };
        }) => renderDisputeType(dispute),
      },
      {
        Header: "Litige",
        minWidth: 200,
        maxWidth: 200,
        Cell: ({
          row: { original: dispute },
        }: {
          row: { original: ShippingRecordDispute };
        }) => <Text>{dispute?.text}</Text>,
      },
      {
        Header: "Auteur",
        minWidth: 120,
        maxWidth: 120,
        Cell: ({
          row: { original: dispute },
        }: {
          row: { original: ShippingRecordDispute };
        }) => <Text>{dispute?.createdBy || ""}</Text>,
      },
      {
        Header: "Date de clôture",
        minWidth: 120,
        maxWidth: 120,
        Cell: ({
          row: { original: dispute },
        }: {
          row: { original: ShippingRecordDispute };
        }) => {
          return (
            <Text>
              {dispute?.closedAt
                ? dateformat(dispute?.closedAt, DEFAULT_DATE_FORMAT)
                : "Ouvert"}
            </Text>
          );
        },
      },
      {
        Header: "Document(s)",
        minWidth: 120,
        maxWidth: 120,
        Cell: ({
          row: { original: dispute },
        }: {
          row: { original: ShippingRecordDispute };
        }) => {
          return <Text>{dispute?._count?.files || 0}</Text>;
        },
      },
      {
        Header: "Actions",
        minWidth: 10,
        maxWidth: 10,
        Cell: () => null,
      },
    ],
    [renderDisputeType]
  );

  return (
    <>
      <InfoContainer
        className={classes.paperContainer}
        isToggleable
        leftHeader={
          <div className={classes.infoHeader}>
            <ReportProblemOutlinedIcon />
            <Text type="h3">Litige(s)</Text>
          </div>
        }
        rightHeader={
          <UIButton
            style={{ textTransform: "none" }}
            onClick={handleAddDispute}
            disabled={!canAddDispute(shippingRecord) || disabled}
            className={classes.textButton}
          >
            <AddIcon />
            Ajouter un litige
          </UIButton>
        }
      >
        <div>
          <JTable
            data={sortedDisputes || []}
            columns={columns}
            setFilter={() => {}}
            setPageIndex={() => {}}
            pageCount={1}
            pageIndex={0}
            onClickAction={() => {
              if (disabled) {
                return;
              }
              toggleAddDisputeDrawer(true);
            }}
            setSelectedItem={setSelectedDispute}
            hideCheckboxes
            hideFilters
            noResultsText="Aucun litige dans ce dossier"
          />
        </div>
      </InfoContainer>
      <AddDisputePopin
        isOpen={!disabled && isAddDisputeDrawerOpen}
        onClose={() => {
          toggleAddDisputeDrawer(false);
          setSelectedDispute(undefined);
        }}
        onSubmit={handleSubmit}
        shippingRecord={shippingRecord}
        shippingRecordDisputes={sortedDisputes}
        selectedDispute={selectedDispute}
      />
    </>
  );
}

export default ShippingRecordDisputeTableBlock;

const useStyles = createUseStyles(() => ({
  disputeTypeContainer: {
    display: "flex",
    flexDirection: "column",
  },
}));
